import {Link} from "preact-router/match";
import {iconCartV2, typeClinicalAnalysis, typeVaccine, typeVaccineCombo} from "../../assets";
import s from "./productCardV3.module.css";
import {useAppContext, useBehavioralContext} from '../../contexts';
import PriceBlock from "./PriceBlock";
import {useProductCardV3} from "./useProductCardV3";
import ProductVendorBadge from "./ProductVendorBadge";
import ServiceTypeBadge from "./ServiceTypeBadge";

const ProductCard = (props) => {
    // Destructured props
    let {
        product,
        handleClick,
        dataTestId = "generic-card",
        additionalRootClasses = "border-[#eee]",
        enableHoverAnimation = true,
        whiteLabel = false,
        vendor = "DEFAULT"
    } = props;

    // Component logic data
    const componentProps = useProductCardV3({
        product
    });

    // Destructured component logic data
    const {
        isButtonDisabled,
        handleSoldOutClick
    } = componentProps;

    // Sets the background color of the service type badge
    const labelBadgeColor =
        product.productLabel ? (
            product.productLabel.toLowerCase() === "novo serviço" ? "yellowBadge"
                : product.productLabel.toLowerCase() === "menor preço" ? "greenBadge"
                    : "redBadge"
        ) : "";

    // Formats the product href without the .html suffix
    const formattedHref =
        product.href ? product.href.split(".html")[0] : "";

    // An enum for all the icon types
    const serviceTypeIconEnum = {
        clinical_analysis: typeClinicalAnalysis,
        vaccine_combo: typeVaccineCombo,
        vaccine: typeVaccine
    }

    // Accessibility feature data
    const {highContrast} = useAppContext();

    // The badge component to be displayed at the top right of the product card
    const productLabel = {
        DEFAULT: <ServiceTypeBadge
            product={product}
            labelBadgeColor={labelBadgeColor}
            formattedHref={formattedHref}
        />,
        RITA: <ProductVendorBadge
            formattedHref={formattedHref}
            tablePrice={product.tablePrice}
        />
    };

    // The variable name says it all
    const productIcon = (
        <div className={s.cardIcon}>
            <img
                src={serviceTypeIconEnum[product.icon]}
                alt={"Card icon"}
                data-testid={`product-card-icon-${formattedHref}`}
                className='dark:grayscale'
            />
        </div>
    );

    // The variable name says it all
    const productName = (
        <h1
            className={`text-ellipsis overflow-hidden ${s.productName} ${highContrast ? "highTitleFirst" : ""} ${whiteLabel ? "!text-white" : ""}`}
            data-testid={`product-card-name-${formattedHref}`}
        >
            {product.name}
        </h1>
    );

    // The variable name says it all
    const productDescription = (
        <p className={`pt-[1rem] block md:hidden ${s.shortDescription} ${highContrast || whiteLabel ? "!text-white" : ""}`}>
            {product.shortDescription}
        </p>
    );
    // Retrieves the method to access the sidebar cart component's properties
    const {handleCart} = useBehavioralContext();

    return (
        <div
            className={`overflow-hidden rounded-[8px] border flex flex-col md:flex-row justify-between items-stretch relative ${s.productCard} ${additionalRootClasses} ${enableHoverAnimation ? s.hover : ""}`}
            data-testid={dataTestId}
        >
            {productLabel[vendor]}
            <div className={"shrink-0 w-full md:w-[45%] px-6 md:px-6 py-7 relative dark:bg-[#464646] flex flex-col justify-between"}>
                {!!product.icon && (productIcon)}
                <div className={"flex flex-col !text-white"}>
                    {productName}
                    {!product?.inStock ? (
                        <div className={`hidden md:block rounded-lg bg-[#F0F2F7] w-full px-3 md:px-6 py-4`}>
                            <p className={`text-[#9B51DF]`}>
                                Serviço fora de estoque no momento.
                            </p>
                        </div>
                    ) : (
                        <div className={"hidden md:block"}>
                            <PriceBlock
                                product={product}
                                vendor={vendor}
                            />
                        </div>
                    )}
                    {productDescription}
                </div>

                {!product?.inStock ? (
                    <button
                        className={`!hidden md:!flex ${s.buyAlert} select-none ${highContrast ? "highFormButton" : ""} ${isButtonDisabled ? "opacity-40" : ""}`}
                        data-testid={`product-card-button-${formattedHref}`}
                        onClick={() => handleSoldOutClick()}
                    >
                        <p className={"uppercase text-white select-none dark:text-black"}>
                            Avise-me
                        </p>
                    </button>
                ) : (
                    <button
                        className={`!hidden md:!flex ${s.purchaseButton} select-none ${highContrast ? "highFormButton" : ""}`}
                        data-testid={`product-card-button-${formattedHref}`}
                        onClick={() => handleCart("_", "_", {
                            isOpen:         true,
                            newProductData: {},
                            url:            `${formattedHref}`
                        })}
                    >
                        <img
                            src={iconCartV2}
                            alt="Cart icon"
                        />
                        <p className={"uppercase text-white select-none dark:text-black"}>
                            Adquira agora
                        </p>
                    </button>
                )}
                
            </div>
            <div
                className={"w-full bg-light-gray dark:bg-black px-6 md:px-10 py-7 flex flex-row md:flex-col justify-between items-start gap-x-4"}>
                <p className={`hidden md:block pt-[1rem] ${s.shortDescription} ${highContrast ? "!text-white" : ""}`}>
                    {product.shortDescription}
                </p>
                <div className={"block md:hidden w-full"}>
                    <PriceBlock
                        product={product}
                        vendor={vendor}
                    />
                </div>
                <div className={"w-full"}>
                    {!product?.inStock ? (
                        <button
                            className={`!flex md:!hidden ${s.buyAlert} select-none ${highContrast ? "highFormButton" : ""} ${isButtonDisabled ? "opacity-40" : ""}`}
                            data-testid={`product-card-button-${formattedHref}`}
                            onClick={() => handleSoldOutClick()}
                        >
                            <p className={"uppercase text-white select-none dark:text-black"}>
                                Avise-me
                            </p>
                        </button>
                    ) : (
                        <button
                            className={`!flex md:!hidden ${s.purchaseButton} select-none ${highContrast ? "highFormButton" : ""}`}
                            data-testid={`product-card-button-${formattedHref}`}
                            onClick={() => handleCart("_", "_", {
                                isOpen:         true,
                                newProductData: {},
                                url:            `${formattedHref}`
                            })}
                        >
                            <img
                                src={iconCartV2}
                                alt="Cart icon"
                            />
                            <p className={"uppercase text-white select-none dark:text-black"}>
                            Adquira agora
                            </p>
                        </button>
                    )}
                    <Link
                        href={`/${formattedHref}`}
                        onClick={() => handleClick(product)}
                        className={`${s.infoButton} select-none ${highContrast ? "highFormButton" : ""}`}
                        data-testid={`product-card-button-${formattedHref}`}
                    >
                        <p className={"uppercase select-none"}>
                            Saiba mais
                        </p>
                    </Link>
                </div>
            </div>
        </div>
    )
}

export default ProductCard;
